/*
 * Web Push subscription helpers.
 *
 * The flow:
 *   1. parent grants permission (via a button click that calls `subscribePush`)
 *   2. browser returns a PushSubscription object
 *   3. we POST it to /api/push/subscribe so the backend can target it later
 *   4. when the backend sends a push, the service worker shows the notification
 *      and posts a "mlou:push-click" message to the live tab after the tap
 *   5. the React side picks that message up + speaks the line via the TTS audio
 */

import axios from "axios";
import { speak, unlockAudio } from "@/lib/audio";

const http = axios.create({ baseURL: "/api", withCredentials: true });

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export async function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export async function pushStatus() {
  if (!(await pushSupported())) return "unsupported";
  if (Notification.permission === "denied") return "denied";
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return "no_sw";
  const sub = await reg.pushManager.getSubscription();
  return sub ? "subscribed" : "idle";
}

export async function subscribePush() {
  if (!(await pushSupported())) throw new Error("Push non supporté sur ce navigateur.");

  unlockAudio();

  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("Permission refusée.");

  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) throw new Error("Service worker non actif.");

  const { configured, public_key } = (await http.get("/push/public-key")).data;
  if (!configured || !public_key) throw new Error("Web Push pas encore configuré côté serveur.");

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(public_key),
    });
  }

  await http.post("/push/subscribe", sub.toJSON());
  return true;
}

export async function unsubscribePush() {
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  await http.post("/push/unsubscribe", { endpoint: sub.endpoint });
  await sub.unsubscribe();
}

/**
 * Listen for the service worker's notification-tap message and play the
 * Neo/Lyria voice line. Returns a cleanup function.
 */
export function listenForPushTaps() {
  if (typeof navigator === "undefined" || !navigator.serviceWorker) return () => {};
  const handler = (event) => {
    const data = event?.data;
    if (!data || data.type !== "mlou:push-click") return;
    if (data.speak) {
      // Use the browser voice here (no precomputed URL for push lines).
      // It still sounds more "vocal" than nothing; once the parent is in
      // the journey the natural OpenAI voice takes over.
      speak(data.speak, "lyria");
    }
  };
  navigator.serviceWorker.addEventListener("message", handler);
  return () => navigator.serviceWorker.removeEventListener("message", handler);
}
